import React, { useEffect, useMemo, useState } from 'react';
import { Layout, Menu, Typography, Space, Button, Table, Card, Descriptions, Modal, Form, Input, Select, Switch, InputNumber, message, Tag, Tooltip } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined, SafetyOutlined, ThunderboltOutlined, StopOutlined, SettingOutlined, DatabaseOutlined, FileSearchOutlined, GlobalOutlined, ReloadOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import api from '../services/api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('Asia/Shanghai');

const fmtUtcToCst = (isoStr) => {
  if (!isoStr) return '-';
  return dayjs.utc(isoStr).tz('Asia/Shanghai').format('YYYY-MM-DD HH:mm:ss');
};

const { Content, Sider } = Layout;
const { Title, Text } = Typography;

const SupervisionManagement = () => {
  const [selectedKey, setSelectedKey] = useState('rate-limit-records');
  const [collapsed, setCollapsed] = useState(false);

  const menuItems = useMemo(
    () => [
      {
        key: 'rate-limit-records',
        icon: <DatabaseOutlined />,
        label: '速率限制记录',
        description: '查看与删除速率限制明细',
      },
      {
        key: 'ip-blocks',
        icon: <GlobalOutlined />,
        label: 'IP封禁管理',
        description: '封禁/解封IP与查看状态',
      },
      {
        key: 'audit-logs',
        icon: <FileSearchOutlined />,
        label: '安全审计日志',
        description: '查看安全事件审计日志',
      },
      {
        key: 'rate-limit-config',
        icon: <SettingOutlined />,
        label: '速率限制配置',
        description: '创建/修改/删除速率限制配置',
      },
      {
        key: 'cleanup-jobs',
        icon: <ThunderboltOutlined />,
        label: '清理任务与调度器',
        description: '手动执行清理任务与查看调度状态',
      },
      {
        key: 'sessions',
        icon: <SafetyOutlined />,
        label: '会话管理',
        description: '查看与撤销用户会话',
      },
    ],
    []
  );

  const selectedItem = useMemo(() => menuItems.find((m) => m.key === selectedKey), [menuItems, selectedKey]);

  const [loading, setLoading] = useState(false);

  // Rate limit records
  const [rlPage, setRlPage] = useState(1);
  const [rlSize, setRlSize] = useState(20);
  const [rlTotal, setRlTotal] = useState(0);
  const [rlRows, setRlRows] = useState([]);
  const [rlSelectedRowKeys, setRlSelectedRowKeys] = useState([]);
  const [rlFilters, setRlFilters] = useState({ ip_address: '', endpoint_path: '', method: '', blocked: undefined });

  // IP blocks
  const [ipPage, setIpPage] = useState(1);
  const [ipSize, setIpSize] = useState(20);
  const [ipTotal, setIpTotal] = useState(0);
  const [ipRows, setIpRows] = useState([]);
  const [ipFilters, setIpFilters] = useState({ ip_address: '', is_active: undefined });

  // Audit logs
  const [auPage, setAuPage] = useState(1);
  const [auSize, setAuSize] = useState(20);
  const [auTotal, setAuTotal] = useState(0);
  const [auRows, setAuRows] = useState([]);
  const [auFilters, setAuFilters] = useState({ event_type: '', severity: '', ip_address: '', username: '' });

  const [auditDetailOpen, setAuditDetailOpen] = useState(false);
  const [auditDetailRow, setAuditDetailRow] = useState(null);

  // Rate limit config
  const [cfgRows, setCfgRows] = useState([]);
  const [cfgModalOpen, setCfgModalOpen] = useState(false);
  const [cfgEditing, setCfgEditing] = useState(null);
  const [cfgForm] = Form.useForm();

  // IP block modal
  const [ipModalOpen, setIpModalOpen] = useState(false);
  const [ipForm] = Form.useForm();

  // Scheduler/Cleanup
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [cleanupStatus, setCleanupStatus] = useState(null);

  // Sessions
  const [sessionPage, setSessionPage] = useState(1);
  const [sessionSize, setSessionSize] = useState(20);
  const [sessionTotal, setSessionTotal] = useState(0);
  const [sessionRows, setSessionRows] = useState([]);
  const [sessionFilters, setSessionFilters] = useState({ username: '', is_active: undefined, remote_ip: '' });
  const [sessionSelectedRowKeys, setSessionSelectedRowKeys] = useState([]);

  const call = async (method, url, options = {}) => {
    const resp = await api.request({ method, url, ...options });
    return resp?.data;
  };

  const loadRateLimitRecords = async () => {
    setLoading(true);
    try {
      const params = {
        page: rlPage,
        size: rlSize,
      };
      if (rlFilters.ip_address) params.ip_address = rlFilters.ip_address;
      if (rlFilters.endpoint_path) params.endpoint_path = rlFilters.endpoint_path;
      if (rlFilters.method) params.method = rlFilters.method;
      if (typeof rlFilters.blocked === 'boolean') params.blocked = rlFilters.blocked;

      const data = await call('get', '/supervision/rate-limit/records', { params });
      const records = data?.data?.records || [];
      const pagination = data?.data?.pagination || {};
      setRlRows(records);
      setRlTotal(Number(pagination.total || 0));
    } catch (e) {
      message.error(`加载速率限制记录失败: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadIpBlocks = async () => {
    setLoading(true);
    try {
      const params = { page: ipPage, size: ipSize };
      if (ipFilters.ip_address) params.ip_address = ipFilters.ip_address;
      if (typeof ipFilters.is_active === 'boolean') params.is_active = ipFilters.is_active;

      const data = await call('get', '/supervision/ip-blocks/list', { params });
      const blocks = data?.data?.blocks || [];
      const pagination = data?.data?.pagination || {};
      setIpRows(blocks);
      setIpTotal(Number(pagination.total || 0));
    } catch (e) {
      message.error(`加载IP封禁列表失败: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadAuditLogs = async () => {
    setLoading(true);
    try {
      const params = { page: auPage, size: auSize };
      if (auFilters.event_type) params.event_type = auFilters.event_type;
      if (auFilters.severity) params.severity = auFilters.severity;
      if (auFilters.ip_address) params.ip_address = auFilters.ip_address;
      if (auFilters.username) params.username = auFilters.username;

      const data = await call('get', '/supervision/audit-logs/list', { params });
      const logs = data?.data?.logs || [];
      const pagination = data?.data?.pagination || {};
      setAuRows(logs);
      setAuTotal(Number(pagination.total || 0));
    } catch (e) {
      message.error(`加载审计日志失败: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadRateLimitConfig = async () => {
    setLoading(true);
    try {
      const data = await call('get', '/supervision/config/rate-limit');
      setCfgRows(data?.data || []);
    } catch (e) {
      message.error(`加载速率限制配置失败: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadCleanupAndScheduler = async () => {
    setLoading(true);
    try {
      const [s1, s2] = await Promise.all([call('get', '/supervision/scheduler/status'), call('get', '/supervision/cleanup/status')]);
      setSchedulerStatus(s1?.data || null);
      setCleanupStatus(s2?.data || null);
    } catch (e) {
      message.error(`加载调度器/清理任务状态失败: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadSessions = async () => {
    setLoading(true);
    try {
      const params = { page: sessionPage, size: sessionSize };
      if (sessionFilters.username) params.username = sessionFilters.username;
      if (typeof sessionFilters.is_active === 'boolean') params.is_active = sessionFilters.is_active;
      if (sessionFilters.remote_ip) params.remote_ip = sessionFilters.remote_ip;

      const data = await call('get', '/supervision/sessions/list', { params });
      const sessions = data?.data?.sessions || [];
      const pagination = data?.data?.pagination || {};
      setSessionRows(sessions);
      setSessionTotal(Number(pagination.total || 0));
    } catch (e) {
      message.error(`加载会话列表失败: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedKey === 'rate-limit-records') {
      loadRateLimitRecords();
    }
    if (selectedKey === 'ip-blocks') {
      loadIpBlocks();
    }
    if (selectedKey === 'audit-logs') {
      loadAuditLogs();
    }
    if (selectedKey === 'rate-limit-config') {
      loadRateLimitConfig();
    }
    if (selectedKey === 'cleanup-jobs') {
      loadCleanupAndScheduler();
    }
    if (selectedKey === 'sessions') {
      loadSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, rlPage, rlSize, ipPage, ipSize, auPage, auSize, sessionPage, sessionSize]);

  const confirmDanger = (title, content, onOk) => {
    Modal.confirm({
      title,
      content,
      okText: '确认',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk,
    });
  };

  const handleDeleteRateLimitRecords = async () => {
    if (!rlSelectedRowKeys.length) {
      message.warning('请先选择要删除的记录');
      return;
    }

    confirmDanger('确认删除', `确定要删除选中的 ${rlSelectedRowKeys.length} 条速率限制记录吗？`, async () => {
      setLoading(true);
      try {
        await api.request({
          method: 'delete',
          url: '/supervision/rate-limit/records',
          data: rlSelectedRowKeys,
          headers: { 'Content-Type': 'application/json' },
        });
        message.success('删除成功');
        setRlSelectedRowKeys([]);
        await loadRateLimitRecords();
      } catch (e) {
        message.error(`删除失败: ${e?.response?.data?.detail || e.message}`);
      } finally {
        setLoading(false);
      }
    });
  };

  const openCreateConfig = () => {
    setCfgEditing(null);
    cfgForm.resetFields();
    cfgForm.setFieldsValue({
      is_active: true,
      priority: 100,
      block_duration: 300,
    });
    setCfgModalOpen(true);
  };

  const openEditConfig = (row) => {
    setCfgEditing(row);
    cfgForm.resetFields();
    cfgForm.setFieldsValue({
      config_name: row.config_name,
      endpoint_pattern: row.endpoint_pattern,
      method: row.method,
      request_limit: row.request_limit,
      time_window: row.time_window,
      block_duration: row.block_duration,
      is_active: row.is_active,
      priority: row.priority,
    });
    setCfgModalOpen(true);
  };

  const submitConfig = async () => {
    const values = await cfgForm.validateFields();
    setLoading(true);
    try {
      if (cfgEditing?.id) {
        await call('put', `/supervision/config/rate-limit/${cfgEditing.id}`, { data: values });
        message.success('更新成功');
      } else {
        await call('post', '/supervision/config/rate-limit', { data: values });
        message.success('创建成功');
      }
      setCfgModalOpen(false);
      await loadRateLimitConfig();
    } catch (e) {
      message.error(`保存失败: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteConfig = (row) => {
    confirmDanger('确认删除配置', `确定要删除配置“${row.config_name}”吗？`, async () => {
      setLoading(true);
      try {
        await call('delete', `/supervision/config/rate-limit/${row.id}`);
        message.success('删除成功');
        await loadRateLimitConfig();
      } catch (e) {
        message.error(`删除失败: ${e?.response?.data?.detail || e.message}`);
      } finally {
        setLoading(false);
      }
    });
  };

  const openBlockIpModal = () => {
    ipForm.resetFields();
    ipForm.setFieldsValue({ auto_unblock: true, block_duration: 3600 });
    setIpModalOpen(true);
  };

  const submitBlockIp = async () => {
    const values = await ipForm.validateFields();
    confirmDanger('确认封禁IP', `确定要封禁 IP ${values.ip_address} 吗？`, async () => {
      setLoading(true);
      try {
        await call('post', '/supervision/ip-blocks/block', { data: values });
        message.success('封禁成功');
        setIpModalOpen(false);
        await loadIpBlocks();
      } catch (e) {
        message.error(`封禁失败: ${e?.response?.data?.detail || e.message}`);
      } finally {
        setLoading(false);
      }
    });
  };

  const unblockIp = (row) => {
    confirmDanger('确认解封IP', `确定要解封 IP ${row.ip_address} 吗？`, async () => {
      setLoading(true);
      try {
        await call('post', '/supervision/ip-blocks/unblock', { data: { ip_address: row.ip_address } });
        message.success('解封成功');
        await loadIpBlocks();
      } catch (e) {
        message.error(`解封失败: ${e?.response?.data?.detail || e.message}`);
      } finally {
        setLoading(false);
      }
    });
  };

  const runCleanup = (taskType) => {
    const titleMap = {
      rate_limit: '清理速率限制记录',
      ip_blocks: '清理过期IP封禁记录',
      audit_logs: '清理过期审计日志',
      comprehensive: '执行综合清理',
    };
    confirmDanger('确认执行清理任务', `确定要执行“${titleMap[taskType] || taskType}”吗？`, async () => {
      setLoading(true);
      try {
        const data = await call('post', '/supervision/cleanup/execute', { params: { task_type: taskType } });
        message.success(data?.message || '执行成功');
        await loadCleanupAndScheduler();
      } catch (e) {
        message.error(`执行失败: ${e?.response?.data?.detail || e.message}`);
      } finally {
        setLoading(false);
      }
    });
  };

  const restartScheduler = () => {
    confirmDanger('确认重启调度器', '确定要重启调度器吗？重启会短暂中断定时任务执行。', async () => {
      setLoading(true);
      try {
        await call('post', '/supervision/scheduler/restart');
        message.success('重启成功');
        await loadCleanupAndScheduler();
      } catch (e) {
        message.error(`重启失败: ${e?.response?.data?.detail || e.message}`);
      } finally {
        setLoading(false);
      }
    });
  };

  const revokeSession = (row) => {
    confirmDanger('确认撤销会话', `确定要撤销用户“${row.username}”的会话吗？`, async () => {
      setLoading(true);
      try {
        await call('post', '/supervision/sessions/revoke', { data: { session_id: row.id } });
        message.success('会话撤销成功');
        await loadSessions();
      } catch (e) {
        message.error(`撤销失败: ${e?.response?.data?.detail || e.message}`);
      } finally {
        setLoading(false);
      }
    });
  };

  const revokeUserSessions = (row) => {
    confirmDanger('确认撤销用户所有会话', `确定要撤销用户“${row.username}”的所有活跃会话吗？`, async () => {
      setLoading(true);
      try {
        await call('post', '/supervision/sessions/revoke-by-user', { data: { user_id: row.user_id } });
        message.success('用户所有会话撤销成功');
        await loadSessions();
      } catch (e) {
        message.error(`撤销失败: ${e?.response?.data?.detail || e.message}`);
      } finally {
        setLoading(false);
      }
    });
  };

  const batchRevokeSessions = async () => {
    if (!sessionSelectedRowKeys.length) {
      message.warning('请先选择要撤销的会话');
      return;
    }

    confirmDanger('确认批量撤销会话', `确定要撤销选中的 ${sessionSelectedRowKeys.length} 个会话吗？`, async () => {
      setLoading(true);
      try {
        await api.request({
          method: 'delete',
          url: '/supervision/sessions/batch',
          data: sessionSelectedRowKeys,
          headers: { 'Content-Type': 'application/json' },
        });
        message.success('批量撤销成功');
        setSessionSelectedRowKeys([]);
        await loadSessions();
      } catch (e) {
        message.error(`批量撤销失败: ${e?.response?.data?.detail || e.message}`);
      } finally {
        setLoading(false);
      }
    });
  };

  const renderHeader = () => (
    <div style={{ marginBottom: 16 }}>
      <Space direction="vertical" size={2} style={{ width: '100%' }}>
        <Title level={4} style={{ margin: 0 }}>
          {selectedItem?.label || '监管管理'}
        </Title>
        <Text type="secondary">{selectedItem?.description || '监管可视化管理页面'}</Text>
      </Space>
    </div>
  );

  const renderRateLimitRecords = () => (
    <>
      {renderHeader()}

      <Space style={{ marginBottom: 12 }} wrap>
        <Input placeholder="IP包含" value={rlFilters.ip_address} onChange={(e) => setRlFilters((s) => ({ ...s, ip_address: e.target.value }))} style={{ width: 180 }} allowClear />
        <Input placeholder="端点包含" value={rlFilters.endpoint_path} onChange={(e) => setRlFilters((s) => ({ ...s, endpoint_path: e.target.value }))} style={{ width: 220 }} allowClear />
        <Select
          placeholder="方法"
          value={rlFilters.method || undefined}
          onChange={(v) => setRlFilters((s) => ({ ...s, method: v || '' }))}
          style={{ width: 120 }}
          allowClear
          options={[
            { value: 'GET', label: 'GET' },
            { value: 'POST', label: 'POST' },
            { value: 'PUT', label: 'PUT' },
            { value: 'DELETE', label: 'DELETE' },
          ]}
        />
        <Select
          placeholder="是否拦截"
          value={typeof rlFilters.blocked === 'boolean' ? String(rlFilters.blocked) : undefined}
          onChange={(v) => setRlFilters((s) => ({ ...s, blocked: v === undefined ? undefined : v === 'true' }))}
          style={{ width: 140 }}
          allowClear
          options={[
            { value: 'true', label: '是' },
            { value: 'false', label: '否' },
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={() => loadRateLimitRecords()} loading={loading}>
          刷新
        </Button>
        <Button danger icon={<DeleteOutlined />} onClick={handleDeleteRateLimitRecords} disabled={!rlSelectedRowKeys.length}>
          删除所选
        </Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={rlRows}
        rowSelection={{
          selectedRowKeys: rlSelectedRowKeys,
          onChange: setRlSelectedRowKeys,
        }}
        pagination={{
          current: rlPage,
          pageSize: rlSize,
          total: rlTotal,
          showSizeChanger: true,
          onChange: (p, s) => {
            setRlPage(p);
            setRlSize(s);
          },
        }}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 80 },
          { title: 'IP', dataIndex: 'ip_address', width: 140 },
          { title: '方法', dataIndex: 'method', width: 90 },
          { title: '端点', dataIndex: 'endpoint_path' },
          {
            title: '时间',
            dataIndex: 'request_time',
            width: 200,
            render: (v) => fmtUtcToCst(v),
          },
          {
            title: '拦截',
            dataIndex: 'blocked',
            width: 90,
            render: (v) => (v ? <Tag color="red">是</Tag> : <Tag>否</Tag>),
          },
          { title: '原因', dataIndex: 'block_reason', width: 200 },
        ]}
      />
    </>
  );

  const renderIpBlocks = () => (
    <>
      {renderHeader()}

      <Space style={{ marginBottom: 12 }} wrap>
        <Input placeholder="IP包含" value={ipFilters.ip_address} onChange={(e) => setIpFilters((s) => ({ ...s, ip_address: e.target.value }))} style={{ width: 180 }} allowClear />
        <Select
          placeholder="是否活跃"
          value={typeof ipFilters.is_active === 'boolean' ? String(ipFilters.is_active) : undefined}
          onChange={(v) => setIpFilters((s) => ({ ...s, is_active: v === undefined ? undefined : v === 'true' }))}
          style={{ width: 140 }}
          allowClear
          options={[
            { value: 'true', label: '是' },
            { value: 'false', label: '否' },
          ]}
        />
        <Button type="primary" icon={<StopOutlined />} onClick={openBlockIpModal}>
          封禁IP
        </Button>
        <Button icon={<ReloadOutlined />} onClick={() => loadIpBlocks()} loading={loading}>
          刷新
        </Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={ipRows}
        pagination={{
          current: ipPage,
          pageSize: ipSize,
          total: ipTotal,
          showSizeChanger: true,
          onChange: (p, s) => {
            setIpPage(p);
            setIpSize(s);
          },
        }}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 80 },
          { title: 'IP', dataIndex: 'ip_address', width: 140 },
          {
            title: '活跃',
            dataIndex: 'is_active',
            width: 90,
            render: (v) => (v ? <Tag color="green">是</Tag> : <Tag>否</Tag>),
          },
          { title: '封禁原因', dataIndex: 'block_reason', width: 220 },
          {
            title: '封禁时间',
            dataIndex: 'blocked_at',
            width: 200,
            render: (v) => fmtUtcToCst(v),
          },
          {
            title: '解封时间',
            dataIndex: 'unblock_at',
            width: 200,
            render: (v) => fmtUtcToCst(v),
          },
          {
            title: '剩余秒数',
            dataIndex: 'remaining_seconds',
            width: 120,
            render: (v) => (typeof v === 'number' ? v : '-'),
          },
          {
            title: '操作',
            key: 'actions',
            width: 120,
            render: (_, row) => (
              <Space>
                <Button size="small" icon={<SafetyOutlined />} disabled={!row.is_active} onClick={() => unblockIp(row)}>
                  解封
                </Button>
              </Space>
            ),
          },
        ]}
      />

      <Modal title="封禁IP" open={ipModalOpen} onCancel={() => setIpModalOpen(false)} onOk={submitBlockIp} okText="确认封禁" cancelText="取消" okButtonProps={{ danger: true }} confirmLoading={loading} destroyOnClose>
        <Form form={ipForm} layout="vertical">
          <Form.Item name="ip_address" label="IP地址" rules={[{ required: true, message: '请输入IP地址' }]}>
            <Input placeholder="例如: 1.2.3.4" />
          </Form.Item>
          <Form.Item name="block_reason" label="封禁原因">
            <Input placeholder="管理员手动封禁" />
          </Form.Item>
          <Form.Item name="auto_unblock" label="自动解封" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="block_duration" label="封禁时长(秒)">
            <InputNumber min={60} max={86400 * 30} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );

  const renderAuditLogs = () => (
    <>
      {renderHeader()}

      <Space style={{ marginBottom: 12 }} wrap>
        <Input placeholder="事件类型" value={auFilters.event_type} onChange={(e) => setAuFilters((s) => ({ ...s, event_type: e.target.value }))} style={{ width: 180 }} allowClear />
        <Select
          placeholder="严重程度"
          value={auFilters.severity || undefined}
          onChange={(v) => setAuFilters((s) => ({ ...s, severity: v || '' }))}
          style={{ width: 160 }}
          allowClear
          options={[
            { value: 'INFO', label: 'INFO' },
            { value: 'WARNING', label: 'WARNING' },
            { value: 'ERROR', label: 'ERROR' },
            { value: 'CRITICAL', label: 'CRITICAL' },
          ]}
        />
        <Input placeholder="IP包含" value={auFilters.ip_address} onChange={(e) => setAuFilters((s) => ({ ...s, ip_address: e.target.value }))} style={{ width: 180 }} allowClear />
        <Input placeholder="用户名包含" value={auFilters.username} onChange={(e) => setAuFilters((s) => ({ ...s, username: e.target.value }))} style={{ width: 180 }} allowClear />
        <Button icon={<ReloadOutlined />} onClick={() => loadAuditLogs()} loading={loading}>
          刷新
        </Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={auRows}
        expandable={{
          expandedRowRender: (row) => (
            <Descriptions size="small" column={3} bordered>
              <Descriptions.Item label="用户ID">{row.user_id ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="端点">{row.endpoint_path || '-'}</Descriptions.Item>
              <Descriptions.Item label="方法">{row.method || '-'}</Descriptions.Item>
              <Descriptions.Item label="IP">{row.ip_address || '-'}</Descriptions.Item>
              <Descriptions.Item label="用户名">{row.username || '-'}</Descriptions.Item>
              <Descriptions.Item label="事件类型">{row.event_type || '-'}</Descriptions.Item>
            </Descriptions>
          ),
          rowExpandable: () => true,
        }}
        pagination={{
          current: auPage,
          pageSize: auSize,
          total: auTotal,
          showSizeChanger: true,
          onChange: (p, s) => {
            setAuPage(p);
            setAuSize(s);
          },
        }}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 80 },
          {
            title: '时间',
            dataIndex: 'event_time',
            width: 200,
            render: (v) => fmtUtcToCst(v),
          },
          { title: '类型', dataIndex: 'event_type', width: 160 },
          {
            title: '严重程度',
            dataIndex: 'severity',
            width: 120,
            render: (v) => {
              const color = v === 'CRITICAL' || v === 'ERROR' ? 'red' : v === 'WARNING' ? 'orange' : 'blue';
              return <Tag color={color}>{v}</Tag>;
            },
          },
          { title: 'IP', dataIndex: 'ip_address', width: 140 },
          { title: '用户名', dataIndex: 'username', width: 140 },
          {
            title: '端点',
            dataIndex: 'endpoint_path',
            width: 240,
            ellipsis: { showTitle: false },
            render: (v) => (
              <Tooltip title={v || ''} placement="topLeft">
                <span>{v || '-'}</span>
              </Tooltip>
            ),
          },
          { title: '方法', dataIndex: 'method', width: 90 },
          {
            title: '描述',
            dataIndex: 'description',
            ellipsis: { showTitle: false },
            render: (v) => (
              <Tooltip title={v || ''} placement="topLeft">
                <span>{v || '-'}</span>
              </Tooltip>
            ),
          },
          {
            title: '详情',
            key: 'detail',
            width: 110,
            render: (_, row) => (
              <Button
                size="small"
                onClick={() => {
                  setAuditDetailRow(row);
                  setAuditDetailOpen(true);
                }}
              >
                查看
              </Button>
            ),
          },
        ]}
      />

      <Modal
        title={`审计日志详情${auditDetailRow?.id ? ` #${auditDetailRow.id}` : ''}`}
        open={auditDetailOpen}
        onCancel={() => {
          setAuditDetailOpen(false);
          setAuditDetailRow(null);
        }}
        footer={null}
        width={900}
        destroyOnClose
      >
        <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }}>
          <Descriptions.Item label="时间">{fmtUtcToCst(auditDetailRow?.event_time)}</Descriptions.Item>
          <Descriptions.Item label="严重程度">{auditDetailRow?.severity || '-'}</Descriptions.Item>
          <Descriptions.Item label="事件类型">{auditDetailRow?.event_type || '-'}</Descriptions.Item>
          <Descriptions.Item label="IP">{auditDetailRow?.ip_address || '-'}</Descriptions.Item>
          <Descriptions.Item label="用户名">{auditDetailRow?.username || '-'}</Descriptions.Item>
          <Descriptions.Item label="端点">{auditDetailRow?.endpoint_path || '-'}</Descriptions.Item>
          <Descriptions.Item label="方法">{auditDetailRow?.method || '-'}</Descriptions.Item>
          <Descriptions.Item label="描述">{auditDetailRow?.description || '-'}</Descriptions.Item>
        </Descriptions>

        <Card size="small" title="additional_data" style={{ marginBottom: 12 }}>
          <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 8, overflow: 'auto', maxHeight: 360 }}>
            {(() => {
              const v = auditDetailRow?.additional_data;
              if (v === null || v === undefined) return '';
              try {
                if (typeof v === 'string') {
                  return JSON.stringify(JSON.parse(v), null, 2);
                }
                return JSON.stringify(v, null, 2);
              } catch (e) {
                return typeof v === 'string' ? v : String(v);
              }
            })()}
          </pre>
        </Card>
      </Modal>
    </>
  );

  const renderRateLimitConfig = () => (
    <>
      {renderHeader()}

      <Space style={{ marginBottom: 12 }} wrap>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateConfig}>
          新建配置
        </Button>
        <Button icon={<ReloadOutlined />} onClick={() => loadRateLimitConfig()} loading={loading}>
          刷新
        </Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={cfgRows}
        pagination={false}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 80 },
          { title: '名称', dataIndex: 'config_name', width: 160 },
          { title: '端点匹配', dataIndex: 'endpoint_pattern', width: 220 },
          { title: '方法', dataIndex: 'method', width: 90 },
          { title: '限制', dataIndex: 'request_limit', width: 90 },
          { title: '窗口(秒)', dataIndex: 'time_window', width: 110 },
          { title: '封禁(秒)', dataIndex: 'block_duration', width: 110 },
          {
            title: '启用',
            dataIndex: 'is_active',
            width: 90,
            render: (v) => (v ? <Tag color="green">是</Tag> : <Tag>否</Tag>),
          },
          { title: '优先级', dataIndex: 'priority', width: 90 },
          {
            title: '创建时间',
            dataIndex: 'created_at',
            width: 200,
            render: (v) => fmtUtcToCst(v),
          },
          {
            title: '更新时间',
            dataIndex: 'updated_at',
            width: 200,
            render: (v) => fmtUtcToCst(v),
          },
          {
            title: '操作',
            key: 'actions',
            width: 180,
            render: (_, row) => (
              <Space>
                <Button size="small" onClick={() => openEditConfig(row)}>
                  编辑
                </Button>
                <Button size="small" danger onClick={() => deleteConfig(row)}>
                  删除
                </Button>
              </Space>
            ),
          },
        ]}
      />

      <Modal title={cfgEditing?.id ? '编辑配置' : '新建配置'} open={cfgModalOpen} onCancel={() => setCfgModalOpen(false)} onOk={submitConfig} okText="保存" cancelText="取消" confirmLoading={loading} destroyOnClose>
        <Form form={cfgForm} layout="vertical">
          <Form.Item name="config_name" label="配置名称" rules={[{ required: true, message: '请输入配置名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="endpoint_pattern" label="端点匹配(支持通配)" rules={[{ required: true, message: '请输入端点匹配' }]}>
            <Input placeholder="例如: /api/v1/auth/login" />
          </Form.Item>
          <Form.Item name="method" label="HTTP方法" rules={[{ required: true, message: '请选择方法' }]}>
            <Select
              options={[
                { value: '*', label: '*' },
                { value: 'GET', label: 'GET' },
                { value: 'POST', label: 'POST' },
                { value: 'PUT', label: 'PUT' },
                { value: 'DELETE', label: 'DELETE' },
              ]}
            />
          </Form.Item>
          <Form.Item name="request_limit" label="请求次数限制" rules={[{ required: true, message: '请输入限制' }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="time_window" label="时间窗口(秒)" rules={[{ required: true, message: '请输入时间窗口' }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="block_duration" label="触发封禁时长(秒)" rules={[{ required: true, message: '请输入封禁时长' }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="priority" label="优先级(越小越优先)" rules={[{ required: true, message: '请输入优先级' }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_active" label="是否启用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );

  const renderSessions = () => (
    <>
      {renderHeader()}

      <Space style={{ marginBottom: 12 }} wrap>
        <Input placeholder="用户名包含" value={sessionFilters.username} onChange={(e) => setSessionFilters((s) => ({ ...s, username: e.target.value }))} style={{ width: 180 }} allowClear />
        <Select
          placeholder="是否活跃"
          value={typeof sessionFilters.is_active === 'boolean' ? String(sessionFilters.is_active) : undefined}
          onChange={(v) => setSessionFilters((s) => ({ ...s, is_active: v === undefined ? undefined : v === 'true' }))}
          style={{ width: 140 }}
          allowClear
          options={[
            { value: 'true', label: '是' },
            { value: 'false', label: '否' },
          ]}
        />
        <Input placeholder="IP包含" value={sessionFilters.remote_ip} onChange={(e) => setSessionFilters((s) => ({ ...s, remote_ip: e.target.value }))} style={{ width: 180 }} allowClear />
        <Button icon={<ReloadOutlined />} onClick={() => loadSessions()} loading={loading}>
          刷新
        </Button>
        <Button danger icon={<DeleteOutlined />} onClick={batchRevokeSessions} disabled={!sessionSelectedRowKeys.length}>
          批量撤销
        </Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={sessionRows}
        rowSelection={{
          selectedRowKeys: sessionSelectedRowKeys,
          onChange: setSessionSelectedRowKeys,
        }}
        pagination={{
          current: sessionPage,
          pageSize: sessionSize,
          total: sessionTotal,
          showSizeChanger: true,
          onChange: (p, s) => {
            setSessionPage(p);
            setSessionSize(s);
          },
        }}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 80 },
          { title: '用户名', dataIndex: 'username', width: 140 },
          { title: '认证方式', dataIndex: 'auth_method', width: 120 },
          { title: 'IP', dataIndex: 'remote_ip', width: 140 },
          {
            title: '创建时间',
            dataIndex: 'created_at',
            width: 200,
            render: (v) => fmtUtcToCst(v),
          },
          {
            title: '过期时间',
            dataIndex: 'expires_at',
            width: 200,
            render: (v) => fmtUtcToCst(v),
          },
          {
            title: '最后活跃',
            dataIndex: 'last_seen_at',
            width: 200,
            render: (v) => fmtUtcToCst(v),
          },
          {
            title: '活跃',
            dataIndex: 'is_active',
            width: 90,
            render: (v) => (v ? <Tag color="green">是</Tag> : <Tag>否</Tag>),
          },
          {
            title: '操作',
            key: 'actions',
            width: 200,
            render: (_, row) => (
              <Space>
                <Button size="small" onClick={() => revokeSession(row)}>
                  撤销
                </Button>
                <Button size="small" onClick={() => revokeUserSessions(row)}>
                  撤销该用户所有
                </Button>
              </Space>
            ),
          },
        ]}
      />
    </>
  );

  const renderCleanupJobs = () => (
    <>
      {renderHeader()}

      <Space style={{ marginBottom: 12 }} wrap>
        <Button icon={<ReloadOutlined />} onClick={loadCleanupAndScheduler} loading={loading}>
          刷新状态
        </Button>
        <Button danger icon={<ReloadOutlined />} onClick={restartScheduler}>
          重启调度器
        </Button>
      </Space>

      <Card size="small" style={{ marginBottom: 16 }} title="调度器状态">
        <Descriptions size="small" column={3} bordered>
          <Descriptions.Item label="运行中">{schedulerStatus?.scheduler_running ? <Tag color="green">是</Tag> : <Tag color="red">否</Tag>}</Descriptions.Item>
          <Descriptions.Item label="任务数量">{schedulerStatus?.job_count ?? 0}</Descriptions.Item>
          <Descriptions.Item label="提示">{schedulerStatus?.scheduler_running ? '正常运行' : '未运行（可点击重启调度器）'}</Descriptions.Item>
        </Descriptions>

        <div style={{ marginTop: 12 }}>
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={schedulerStatus?.jobs || []}
            locale={{ emptyText: '暂无定时任务（如刚重启，请稍后刷新）' }}
            columns={[
              { title: '任务ID', dataIndex: 'id', width: 220 },
              { title: '名称', dataIndex: 'name', width: 220 },
              {
                title: '下一次执行',
                dataIndex: 'next_run_time',
                width: 220,
                render: (v) => fmtUtcToCst(v),
              },
              { title: '触发器', dataIndex: 'trigger' },
            ]}
          />
        </div>
      </Card>

      <Card size="small" style={{ marginBottom: 16 }} title="清理任务统计">
        <Descriptions size="small" column={3} bordered>
          <Descriptions.Item label="速率限制记录数">{cleanupStatus?.current_counts?.rate_limit_records ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="IP封禁总数">{cleanupStatus?.current_counts?.ip_blocks_total ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="活跃IP封禁">{cleanupStatus?.current_counts?.ip_blocks_active ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="审计日志条数">{cleanupStatus?.current_counts?.audit_logs ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="最近一次清理">{cleanupStatus?.last_cleanup ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="最近清理记录数">{Array.isArray(cleanupStatus?.recent_cleanings) ? cleanupStatus.recent_cleanings.length : 0}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title="手动执行清理任务">
        <Space wrap>
          <Button danger onClick={() => runCleanup('rate_limit')}>
            清理速率限制记录
          </Button>
          <Button danger onClick={() => runCleanup('ip_blocks')}>
            清理过期IP封禁
          </Button>
          <Button danger onClick={() => runCleanup('audit_logs')}>
            清理过期审计日志
          </Button>
          <Button danger onClick={() => runCleanup('comprehensive')}>
            综合清理
          </Button>
        </Space>
        <div style={{ marginTop: 8 }}>
          <Text type="secondary">说明：以上操作会直接影响数据库数据，执行前请确认。</Text>
        </div>
      </Card>
    </>
  );

  const renderContent = () => {
    switch (selectedKey) {
      case 'rate-limit-records':
        return renderRateLimitRecords();
      case 'ip-blocks':
        return renderIpBlocks();
      case 'audit-logs':
        return renderAuditLogs();
      case 'rate-limit-config':
        return renderRateLimitConfig();
      case 'cleanup-jobs':
        return renderCleanupJobs();
      case 'sessions':
        return renderSessions();
      default:
        return renderRateLimitRecords();
    }
  };

  return (
    <div style={{ height: '100vh', background: '#f5f5f5', overflow: 'hidden' }}>
      <Layout style={{ height: '100vh', background: '#f5f5f5', overflow: 'hidden' }}>
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          width={320}
          collapsedWidth={80}
          style={{
            background: '#fff',
            borderRight: '1px solid #f0f0f0',
            boxShadow: '2px 0 8px rgba(0,0,0,0.06)',
            display: 'flex',
            flexDirection: 'column',
          }}
          trigger={null}
        >
          <div
            style={{
              padding: collapsed ? '16px 8px' : '20px 16px 16px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              position: 'relative',
              minHeight: collapsed ? '60px' : 'auto',
            }}
          >
            {!collapsed && (
              <>
                <Title level={4} style={{ color: '#fff', margin: 0, marginBottom: '8px' }}>
                  监管管理
                </Title>
                <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>安全风控与速率限制可视化管理</Text>
              </>
            )}

            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                color: '#fff',
                border: 'none',
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                background: 'rgba(255,255,255,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Menu
              mode="inline"
              selectedKeys={[selectedKey]}
              onClick={({ key }) => setSelectedKey(key)}
              style={{
                border: 'none',
                background: 'transparent',
                padding: collapsed ? '8px 0' : '16px 0',
                flex: 1,
                minHeight: 0,
              }}
              inlineCollapsed={collapsed}
              items={menuItems}
            />
          </div>
        </Sider>

        <Layout style={{ background: '#f5f5f5', padding: '24px', height: '100%' }}>
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <Content style={{ background: 'transparent', padding: 0, height: '100%', overflow: 'auto' }}>{renderContent()}</Content>
          </div>
        </Layout>
      </Layout>
    </div>
  );
};

export default SupervisionManagement;
